import { useEffect, useState } from 'react'
import { useAudio } from '@w/react-audio'
import { useQuery } from '@w/react-query'
import type { BlockEditorHandle } from '@w/content-blocks-editor'
import { Button } from '@w/ui/components/button'
import { IconButton } from '@w/ui/components/icon-button'
import { PauseIcon, PlayIcon, RotateCcwIcon, SkipBackIcon, SkipForwardIcon, XIcon } from '@w/ui/components/icon'
import { cn } from '@w/ui/lib/utils'
import { withoutActWarning } from '@w/utils-fe'
import { useTranslation } from '../i18n'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import { speechTarget, spokenWords, wordAt, type SpeechPlan, type SpokenWord } from './page-speech'
import { chunkKey, type SpeechSession } from './speech-session'

/** Bottom toolbar that reads a page snapshot aloud and paints the spoken word in
 * the editor (`page-listening.spec.md` → Player). Playback runs the plan's ramp
 * from `start`; a jump into audio already on the device plays it from the unit. */
export function PageListenBar({ plan, session, editor, onClose }: {
  plan: SpeechPlan
  session: SpeechSession
  editor: BlockEditorHandle | null
  onClose: () => void
}) {
  const { t } = useTranslation('listen')
  const routerCtx = useRouterContext()
  // `start`: piece the ramp began at; `index`: chunk on that ramp; `seekMs`: where
  // the chunk starts playing; `paused`: the listener's pause, which holds the
  // narration's rest (a paused chunk never ends, so every chunk loads playing);
  // `resting`: in the narration's pause after the chunk.
  const [position, setPosition] = useState<Position>({ start: 0, index: 0, seekMs: 0, paused: false, resting: false, finished: false })
  const chunks = plan.chunksFrom(position.start)
  const chunk = chunks[position.index]
  const speech = useQuery({
    // Keyed by position too: every chunk played gets its own buffer, so the
    // player reloads even when two chunks in a row have the same text.
    queryKey: ['page-speech', chunkKey(chunk), position.start, position.index],
    queryFn: async () => {
      const voiced = await session.load(chunk)
      return { buffer: voiced.audio.slice().buffer, timings: voiced.words }
    },
    staleTime: Infinity,
    // The transport retries transient failures; a voice failure shows retry at once.
    retry: false,
  })
  // Aligned per chunk, not cached by text: equal texts sit in different blocks.
  const words = speech.data ? spokenWords(chunk, speech.data.timings) : []
  const audio = useAudio(speech.data && !position.finished && !position.resting ? {
    arrayBuffer: speech.data.buffer,
    autoplay: true,
    initialProgress: position.seekMs,
    onEnd: () => {
      if (chunk.pauseMs > 0 && position.index < chunks.length - 1) setPosition({ ...position, resting: true })
      else setPosition(advanced(position, chunks.length))
    },
  } : undefined, routerCtx.testSupport?.audioOptions)

  useEffect(() => {
    const timer = position.resting && !position.paused ? setTimeout(() => { withoutActWarning(() => { setPosition(advanced(position, chunks.length)) }) }, chunk.pauseMs) : undefined
    return () => { clearTimeout(timer) }
  }, [position, chunk.pauseMs, chunks.length])
  useEffect(() => {
    // Opening the player is the intent to listen: the next ~20 s are prepared at once.
    session.prepareAfter(chunks, position.index)
  }, [chunks, position.index, session])

  // During a rest the audio is released; the chunk's last word stays spoken.
  const word = position.finished ? undefined : position.resting ? words.at(-1) : wordAt(words, audio.progress)
  const target = word && speechTarget(word)
  const blockId = target?.blockId
  const wordText = target?.word?.text
  const wordOffset = target?.word?.offset
  useEffect(() => {
    editor?.highlightSpeech(blockId === undefined ? null
      : { blockId, ...(wordText !== undefined && wordOffset !== undefined && { word: { text: wordText, offset: wordOffset } }) })
  }, [editor, blockId, wordText, wordOffset])
  useEffect(() => () => { editor?.highlightSpeech(null) }, [editor])

  /** Plays from a unit: inside the page's first-listen chunks when that chunk is
   * on the device, otherwise on a new ramp starting at the unit. */
  const playFrom = async (unitStart: number) => {
    const canonical = plan.chunksFrom(0)
    const index = canonical.findLastIndex(candidate => candidate.from <= unitStart)
    const startOf = (spoken: SpokenWord[]) => spoken.find(candidate => candidate.piece >= unitStart)?.startMs ?? 0
    if (position.start === 0 && position.index === index && audio.ready) {
      // The chunk is loaded and unchanged state would not reload it: seek inside it.
      audio.setProgress(startOf(words))
      setPosition({ ...position, paused: false })
      void audio.play()
      return
    }
    // Still loading, or another chunk: a new position loads it at the unit.
    // Cached word timestamps are unknown until the browser store read supplies the seek offset.
    const voiced = await session.peek(canonical[index])
    setPosition(voiced
      ? { start: 0, index, seekMs: startOf(spokenWords(canonical[index], voiced.words)), paused: false, resting: false, finished: false }
      : { start: unitStart, index: 0, seekMs: 0, paused: false, resting: false, finished: false })
  }
  const spokenPiece = word?.piece ?? chunk.from
  const unit = plan.unitStarts.findLastIndex(start => start <= spokenPiece)
  const failed = speech.isError || audio.status === 'error'
  const preparing = !failed && !position.finished && !position.resting && (speech.isPending || audio.status === 'loading')
  const playing = audio.playing || (position.resting && !position.paused)

  // Progress spans the whole page, weighted by text.
  const offsets = plan.pieces.map((_, index) => plan.pieces.slice(0, index).reduce((sum, piece) => sum + piece.text.length, 0))
  const total = plan.pieces.reduce((sum, piece) => sum + piece.text.length, 0)
  const chunkEnd = chunk.from + chunk.pieces.length
  const share = position.resting ? 1 : audio.duration > 0 ? Math.min(1, audio.progress / audio.duration) : 0
  const heard = position.finished ? total
    : (offsets[chunk.from] ?? 0) + share * ((offsets[chunkEnd] ?? total) - (offsets[chunk.from] ?? 0))
  const percent = total > 0 ? Math.round(heard / total * 100) : 0

  return (
    <div className="sticky bottom-0 z-10 flex w-full max-w-[700px] px-4 pb-4" data-testid="flashcards-listen-bar">
      <div className="border-border bg-surface-2 flex w-full flex-col gap-2 rounded-2xl border px-3 pt-3 pb-2 shadow-lg">
        <button
          aria-label={t('listen')}
          className="group flex h-4 w-full cursor-pointer items-center"
          data-testid="flashcards-listen-progress"
          type="button"
          onClick={event => {
            const rect = event.currentTarget.getBoundingClientRect()
            const at = rect.width > 0 ? (event.clientX - rect.left) / rect.width * total : 0
            void playFrom(plan.unitStarts.findLast(start => (offsets[start] ?? 0) <= at) ?? 0)
          }}
        >
          <span className="bg-surface-3 relative h-1 w-full overflow-hidden rounded-full transition-[height] group-hover:h-1.5">
            <span className="bg-primary absolute inset-y-0 left-0 rounded-full" style={{ width: `${percent}%` }} />
          </span>
        </button>
        <div className="flex items-center justify-between gap-2">
          <p aria-live="polite" className="text-on-surface-secondary min-w-0 flex-1 truncate text-xs tabular-nums" data-testid="flashcards-listen-status">
            {failed ? <span className="text-error">{t('failed')}</span>
              : preparing ? t('preparing')
              : t('progress', { percent: String(percent) })}
          </p>
          <div className="flex items-center gap-1">
            <IconButton aria-label={t('previous')} data-testid="flashcards-listen-previous" disabled={unit <= 0} semanticRole="secondary" type="button" variant="text"
              onClick={() => { void playFrom(plan.unitStarts[unit - 1] ?? 0) }}>
              <SkipBackIcon />
              {t('previous')}
            </IconButton>
            {failed ? (
              <Button data-testid="flashcards-listen-retry" semanticRole="primary" size="sm" variant="filled" onClick={() => { /* Only the server can supply generated audio and its word timestamps. */ void speech.refetch() }}>
                {t('retry')}
              </Button>
            ) : (
              <IconButton
                aria-label={position.finished ? t('replay') : playing ? t('pause') : t('play')}
                className={cn(preparing && 'animate-pulse')}
                data-testid="flashcards-listen-toggle"
                data-listen-status={position.finished ? 'finished' : audio.status}
                loading={preparing}
                semanticRole="primary"
                type="button"
                variant="filled"
                onClick={() => {
                  if (position.finished) { void playFrom(0); return }
                  // Remembered, so neither the rest nor the next chunk resumes on its own.
                  setPosition({ ...position, paused: playing })
                  if (playing) audio.pause()
                  else if (!position.resting) void audio.play()
                }}
              >
                {position.finished ? <RotateCcwIcon /> : playing ? <PauseIcon /> : <PlayIcon />}
                {position.finished ? t('replay') : playing ? t('pause') : t('play')}
              </IconButton>
            )}
            <IconButton aria-label={t('next')} data-testid="flashcards-listen-next" disabled={unit >= plan.unitStarts.length - 1} semanticRole="secondary" type="button" variant="text"
              onClick={() => { void playFrom(plan.unitStarts[unit + 1] ?? spokenPiece) }}>
              <SkipForwardIcon />
              {t('next')}
            </IconButton>
          </div>
          <div className="flex flex-1 justify-end">
            <IconButton aria-label={t('close')} data-testid="flashcards-listen-close" semanticRole="secondary" size="sm" type="button" variant="text" onClick={onClose}>
              <XIcon />
              {t('close')}
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  )
}

type Position = { start: number; index: number; seekMs: number; paused: boolean; resting: boolean; finished: boolean }

/** Where playback goes when the chunk (and its rest) ends: the next chunk, or done. */
function advanced(position: Position, chunkCount: number): Position {
  return position.index < chunkCount - 1
    ? { ...position, index: position.index + 1, seekMs: 0, resting: false }
    : { ...position, resting: false, finished: true }
}
